import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Search, Plus, Download, FileText, FlaskConical, TrendingUp, IndianRupee } from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, BarChart, Bar, Legend,
} from 'recharts';
import api from '@/lib/api';
import PageHeader from '@/components/ui/PageHeader';
import DataTable, { Column } from '@/components/ui/DataTable';
import PillBadge from '@/components/ui/PillBadge';
import ChartCard from '@/components/ui/ChartCard';
import Modal from '@/components/ui/Modal';
import SideDrawer from '@/components/ui/SideDrawer';
import { ChartSkeleton } from '@/components/ui/LoadingSkeleton';
import TagChip from '@/components/ui/TagChip';
import { format } from 'date-fns';
import { useTableExport } from '@/hooks/useTableExport';

interface TestRequest {
  id: string;
  requestCode: string;
  title: string;
  type: string;
  status: string;
  priority: string;
  lab?: { name: string };
  partner?: { name: string };
  requester: { name: string };
  requestDate: string;
  dueDate: string;
  completedDate?: string;
  quoteAmount?: number;
  parameters: string[];
  remarks?: string;
}

const MOCK_REQUESTS: TestRequest[] = [
  // 2023 records
  {
    id: 'mock-req-2023-01', requestCode: 'REQ-2023-001', title: 'Stability Study — Metformin HCl 500mg 24-Month',
    type: 'Stability', status: 'completed', priority: 'normal',
    lab: { name: 'Mumbai QC Central' },
    requester: { name: 'Dr. Anjali Mehta' },
    requestDate: '2023-02-10', dueDate: '2023-12-10', completedDate: '2023-12-05',
    quoteAmount: 72000,
    parameters: ['Assay', 'Related Substances', 'Dissolution', 'Moisture Content', 'Microbial Limits'],
    remarks: 'ICH Q1A(R2) long-term and accelerated conditions; all timepoints compliant',
  },
  {
    id: 'mock-req-2023-02', requestCode: 'REQ-2023-002', title: 'Method Validation — GC Residual Solvents for Amlodipine API',
    type: 'Method Validation', status: 'completed', priority: 'high',
    partner: { name: 'Vimta Labs Limited' },
    requester: { name: 'Ramesh Kulkarni' },
    requestDate: '2023-04-18', dueDate: '2023-06-30', completedDate: '2023-06-22',
    quoteAmount: 38500,
    parameters: ['Specificity', 'Linearity', 'Accuracy', 'Precision', 'LOD/LOQ', 'Robustness'],
    remarks: 'ICH Q2(R1) compliant; method transferred to QC team post-validation',
  },
  {
    id: 'mock-req-2023-03', requestCode: 'REQ-2023-003', title: 'Batch Release Testing — Atorvastatin 40mg Tablets',
    type: 'Release Testing', status: 'completed', priority: 'urgent',
    lab: { name: 'Pune Formulation Lab' },
    requester: { name: 'Priya Nair' },
    requestDate: '2023-05-03', dueDate: '2023-05-17', completedDate: '2023-05-15',
    quoteAmount: 16800,
    parameters: ['Identification', 'Assay', 'Dissolution', 'Uniformity of Dosage Units', 'Microbial Limits'],
    remarks: 'Expedited release for export to UAE; passed all specifications',
  },
  {
    id: 'mock-req-2023-04', requestCode: 'REQ-2023-004', title: 'Microbiological Testing — Ophthalmic Drops (Ciprofloxacin 0.3%)',
    type: 'Microbiology', status: 'completed', priority: 'high',
    lab: { name: 'Chennai Sterile Lab' },
    requester: { name: 'Arun Menon' },
    requestDate: '2023-07-11', dueDate: '2023-08-05', completedDate: '2023-07-30',
    quoteAmount: 29000,
    parameters: ['Sterility Test', 'Bacterial Endotoxins', 'Particulate Matter', 'pH'],
    remarks: 'Sterility confirmed; endotoxin levels within USP limits',
  },
  {
    id: 'mock-req-2023-05', requestCode: 'REQ-2023-005', title: 'Analytical Method Development — Dissolution for Ibuprofen SR',
    type: 'Analytical', status: 'completed', priority: 'normal',
    lab: { name: 'Bengaluru R&D Analytical Centre' },
    requester: { name: 'Meena Iyer' },
    requestDate: '2023-08-20', dueDate: '2023-10-31', completedDate: '2023-10-25',
    quoteAmount: 45000,
    parameters: ['Method Development', 'Media Optimisation', 'Apparatus Selection', 'Discriminatory Ability'],
  },
  {
    id: 'mock-req-2023-06', requestCode: 'REQ-2023-006', title: 'Regulatory Submission Testing — Losartan Potassium 50mg',
    type: 'Regulatory', status: 'completed', priority: 'high',
    lab: { name: 'Mumbai QC Central' },
    requester: { name: 'Vijay Sharma' },
    requestDate: '2023-10-05', dueDate: '2023-12-20', completedDate: '2023-12-15',
    quoteAmount: 95000,
    parameters: ['Full Specification Testing', 'Impurity Profiling', 'Genotoxic Impurities', 'Stability Commitment'],
    remarks: 'CDSCO NDA dossier submission; all data generated per ICH M7',
  },
  // 2024 records
  {
    id: 'mock-req-2024-01', requestCode: 'REQ-2024-001', title: 'Stability Study — Pantoprazole 40mg Enteric Coated 18-Month',
    type: 'Stability', status: 'completed', priority: 'high',
    lab: { name: 'Mumbai QC Central' },
    requester: { name: 'Dr. Anjali Mehta' },
    requestDate: '2024-01-08', dueDate: '2024-09-08', completedDate: '2024-09-01',
    quoteAmount: 88000,
    parameters: ['Assay', 'Degradation Products', 'Dissolution', 'Appearance', 'Microbial Limits'],
    remarks: 'Enteric coat integrity maintained across all time points; 18-month data submitted',
  },
  {
    id: 'mock-req-2024-02', requestCode: 'REQ-2024-002', title: 'Method Validation — HPLC Impurity Profiling for Azithromycin',
    type: 'Method Validation', status: 'completed', priority: 'normal',
    partner: { name: 'Vimta Labs Limited' },
    requester: { name: 'Deepak Rao' },
    requestDate: '2024-02-14', dueDate: '2024-04-30', completedDate: '2024-04-25',
    quoteAmount: 52000,
    parameters: ['Specificity', 'Linearity', 'Accuracy', 'Precision', 'LOD/LOQ'],
  },
  {
    id: 'mock-req-2024-03', requestCode: 'REQ-2024-003', title: 'Batch Release Testing — Insulin Glargine Injection 100 IU/mL',
    type: 'Release Testing', status: 'completed', priority: 'urgent',
    lab: { name: 'Chennai Sterile Lab' },
    requester: { name: 'Suresh Patil' },
    requestDate: '2024-03-01', dueDate: '2024-03-18', completedDate: '2024-03-16',
    quoteAmount: 24500,
    parameters: ['Identity', 'Potency', 'Sterility', 'Bacterial Endotoxins', 'pH', 'Particulate Matter'],
    remarks: 'Critical biologics batch — sterility confirmed; potency 99.4 IU/mL',
  },
  {
    id: 'mock-req-2024-04', requestCode: 'REQ-2024-004', title: 'Microbiology Environmental Monitoring — Cleanroom Class B/C',
    type: 'Microbiology', status: 'completed', priority: 'high',
    lab: { name: 'Delhi Contract Microbiology Lab' },
    requester: { name: 'Kavitha Nair' },
    requestDate: '2024-04-10', dueDate: '2024-05-10', completedDate: '2024-05-08',
    quoteAmount: 18000,
    parameters: ['Active Air Sampling', 'Settle Plates', 'Surface Monitoring', 'Personnel Monitoring'],
    remarks: 'Quarterly EM campaign; one isolate recovered — CAPA initiated',
  },
  {
    id: 'mock-req-2024-05', requestCode: 'REQ-2024-005', title: 'Release Testing — Dexamethasone 8mg IV Injection',
    type: 'Release Testing', status: 'completed', priority: 'urgent',
    lab: { name: 'Chennai Sterile Lab' },
    requester: { name: 'Arun Menon' },
    requestDate: '2024-06-03', dueDate: '2024-06-20', completedDate: '2024-06-18',
    quoteAmount: 21500,
    parameters: ['Assay', 'Sterility', 'Endotoxins', 'Particulate Matter', 'pH', 'Osmolality'],
  },
  {
    id: 'mock-req-2024-06', requestCode: 'REQ-2024-006', title: 'Method Validation — ICP-OES Elemental Impurities (ICH Q3D)',
    type: 'Method Validation', status: 'completed', priority: 'high',
    lab: { name: 'Hyderabad API Lab' },
    requester: { name: 'Harish Reddy' },
    requestDate: '2024-07-15', dueDate: '2024-10-15', completedDate: '2024-10-10',
    quoteAmount: 67000,
    parameters: ['Arsenic', 'Cadmium', 'Lead', 'Mercury', 'Nickel', 'Palladium'],
    remarks: 'ICH Q3D Class 1 & 2A elements; all below PDEs with adequate safety margin',
  },
  {
    id: 'mock-req-2024-07', requestCode: 'REQ-2024-007', title: 'Stability Study — Clopidogrel 75mg Accelerated 6-Month',
    type: 'Stability', status: 'completed', priority: 'normal',
    lab: { name: 'Kolkata Stability Chamber Facility' },
    requester: { name: 'Sonal Desai' },
    requestDate: '2024-08-01', dueDate: '2025-02-01', completedDate: '2025-01-28',
    quoteAmount: 56000,
    parameters: ['Assay', 'Related Substances', 'Dissolution', 'Water Content', 'Appearance'],
  },
  {
    id: 'mock-req-2024-08', requestCode: 'REQ-2024-008', title: 'Analytical — Particle Size Distribution for Inhalation Powder',
    type: 'Analytical', status: 'completed', priority: 'normal',
    lab: { name: 'Bengaluru R&D Analytical Centre' },
    requester: { name: 'Arjun Pillai' },
    requestDate: '2024-09-20', dueDate: '2024-11-05', completedDate: '2024-10-30',
    quoteAmount: 33000,
    parameters: ['MMAD', 'GSD', 'Fine Particle Fraction', 'Delivered Dose Uniformity'],
    remarks: 'Cascade impactor (NGI) method; FPF 42% — within target range',
  },
  // 2025 records
  {
    id: 'mock-req-1', requestCode: 'REQ-2025-001', title: 'Stability Study — Amoxicillin 500mg 12-Month',
    type: 'Stability', status: 'in_progress', priority: 'high',
    lab: { name: 'Mumbai QC Central' },
    requester: { name: 'Dr. Anjali Mehta' },
    requestDate: '2025-01-10', dueDate: '2025-07-10',
    quoteAmount: 85000,
    parameters: ['Assay', 'Dissolution', 'Related Substances', 'Moisture Content', 'Microbial Limits'],
    remarks: 'ICH Q1A(R2) conditions: 25°C/60% RH (long-term) and 40°C/75% RH (accelerated)',
  },
  {
    id: 'mock-req-2', requestCode: 'REQ-2025-002', title: 'Method Validation — HPLC Assay for Ciprofloxacin',
    type: 'Method Validation', status: 'submitted', priority: 'normal',
    partner: { name: 'Vimta Labs Limited' },
    requester: { name: 'Ramesh Kulkarni' },
    requestDate: '2025-03-15', dueDate: '2025-05-30',
    quoteAmount: 42000,
    parameters: ['Specificity', 'Linearity', 'Accuracy', 'Precision', 'LOD/LOQ'],
  },
  {
    id: 'mock-req-3', requestCode: 'REQ-2025-003', title: 'Batch Release Testing — Paracetamol 650mg ER',
    type: 'Release Testing', status: 'completed', priority: 'urgent',
    lab: { name: 'Pune Formulation Lab' },
    requester: { name: 'Priya Nair' },
    requestDate: '2025-02-05', dueDate: '2025-02-20', completedDate: '2025-02-18',
    quoteAmount: 18500,
    parameters: ['Identification', 'Assay', 'Dissolution', 'Hardness', 'Disintegration'],
    remarks: 'Priority release for export shipment deadline',
  },
  {
    id: 'mock-req-4', requestCode: 'REQ-2025-004', title: 'Microbiological Limit Test — Injectable Saline',
    type: 'Microbiology', status: 'under_review', priority: 'high',
    lab: { name: 'Chennai Sterile Lab' },
    requester: { name: 'Arun Menon' },
    requestDate: '2025-04-02', dueDate: '2025-04-25',
    quoteAmount: 32000,
    parameters: ['Sterility Test', 'Bacterial Endotoxins', 'Particulate Matter', 'Container Closure Integrity'],
  },
  {
    id: 'mock-req-2025-05', requestCode: 'REQ-2025-005', title: 'Release Testing — Ondansetron 4mg ODT Batch B25041',
    type: 'Release Testing', status: 'in_progress', priority: 'high',
    lab: { name: 'Pune Formulation Lab' },
    requester: { name: 'Suresh Patil' },
    requestDate: '2025-04-10', dueDate: '2025-04-28',
    quoteAmount: 15500,
    parameters: ['Identification', 'Assay', 'Disintegration', 'Uniformity', 'Microbial Limits'],
  },
  {
    id: 'mock-req-2025-06', requestCode: 'REQ-2025-006', title: 'Regulatory Package — Tenofovir Disoproxil 300mg (WHO Prequalification)',
    type: 'Regulatory', status: 'in_progress', priority: 'urgent',
    lab: { name: 'Mumbai QC Central' },
    requester: { name: 'Vijay Sharma' },
    requestDate: '2025-03-01', dueDate: '2025-08-31',
    quoteAmount: 120000,
    parameters: ['Full Spec Testing', 'Impurity Profiling', 'Genotoxic Impurities', 'Comparative Dissolution', 'Stability Data Package'],
    remarks: 'WHO PQ dossier; comparative dissolution vs originator (Viread) in 3 media',
  },
  {
    id: 'mock-req-2025-07', requestCode: 'REQ-2025-007', title: 'Method Validation — Bioanalytical LC-MS/MS for Atorvastatin',
    type: 'Method Validation', status: 'submitted', priority: 'high',
    lab: { name: 'Bengaluru R&D Analytical Centre' },
    requester: { name: 'Dr. Preeti Joshi' },
    requestDate: '2025-04-05', dueDate: '2025-06-15',
    quoteAmount: 78000,
    parameters: ['Selectivity', 'Calibration Curve', 'Accuracy & Precision', 'Matrix Effect', 'Stability'],
    remarks: 'EMA Bioanalytical Method Validation guidelines; plasma matrix (human)',
  },
  {
    id: 'mock-req-2025-08', requestCode: 'REQ-2025-008', title: 'Stability Study — Escitalopram 10mg Film-Coated Tablets',
    type: 'Stability', status: 'submitted', priority: 'normal',
    lab: { name: 'Kolkata Stability Chamber Facility' },
    requester: { name: 'Sonal Desai' },
    requestDate: '2025-04-12', dueDate: '2026-04-12',
    quoteAmount: 64000,
    parameters: ['Assay', 'Related Substances', 'Dissolution', 'Hardness', 'Appearance'],
  },
  {
    id: 'mock-req-2025-09', requestCode: 'REQ-2025-009', title: 'Analytical — Genotoxic Impurity Testing (Nitrosamines) for Valsartan',
    type: 'Analytical', status: 'under_review', priority: 'urgent',
    lab: { name: 'Hyderabad API Lab' },
    requester: { name: 'Harish Reddy' },
    requestDate: '2025-02-20', dueDate: '2025-04-20',
    quoteAmount: 55000,
    parameters: ['NDMA', 'NDEA', 'NMBA', 'NDIPA', 'NDBA'],
    remarks: 'USFDA and EMA nitrosamine assessment; GC-MS/MS method; limits per ICH M7',
  },
  {
    id: 'mock-req-2025-10', requestCode: 'REQ-2025-010', title: 'Release Testing — Meropenem 1g IV Powder (Sterile)',
    type: 'Release Testing', status: 'rejected', priority: 'high',
    lab: { name: 'Chennai Sterile Lab' },
    requester: { name: 'Kavitha Nair' },
    requestDate: '2025-01-25', dueDate: '2025-02-10',
    quoteAmount: 28000,
    parameters: ['Identity', 'Assay', 'Sterility', 'Endotoxins', 'Reconstitution Time'],
    remarks: 'Batch rejected — assay OOS (94.2% vs spec 98.0-102.0%); investigation ongoing',
  },
  {
    id: 'mock-req-2025-11', requestCode: 'REQ-2025-011', title: 'Microbiology — Water System Validation (PW & WFI)',
    type: 'Microbiology', status: 'in_progress', priority: 'normal',
    lab: { name: 'Delhi Contract Microbiology Lab' },
    requester: { name: 'Arjun Pillai' },
    requestDate: '2025-03-10', dueDate: '2025-05-10',
    quoteAmount: 22000,
    parameters: ['Total Viable Count', 'Conductivity', 'TOC', 'Nitrate', 'Heavy Metals'],
    remarks: 'Phase 2 intensive monitoring (4 weeks); all sampling points per USP <1231>',
  },
  {
    id: 'mock-req-2025-12', requestCode: 'REQ-2025-012', title: 'Batch Release Testing — Doxorubicin Liposomal 2mg/mL Injection',
    type: 'Release Testing', status: 'cancelled', priority: 'urgent',
    lab: { name: 'Chennai Sterile Lab' },
    requester: { name: 'Dr. Preeti Joshi' },
    requestDate: '2025-02-28', dueDate: '2025-03-14',
    quoteAmount: 38000,
    parameters: ['Assay', 'Encapsulation Efficiency', 'Particle Size', 'Sterility', 'pH'],
    remarks: 'Cancelled — manufacturing batch withdrawn due to particle size OOS at filling stage',
  },
  // 2026 records
  {
    id: 'mock-req-2026-01', requestCode: 'REQ-2026-001', title: 'Method Validation — qPCR Mycoplasma Detection for Cell-Based Products',
    type: 'Method Validation', status: 'submitted', priority: 'high',
    lab: { name: 'Chennai Sterile Lab' },
    requester: { name: 'Dr. Preeti Joshi' },
    requestDate: '2026-01-15', dueDate: '2026-04-15',
    quoteAmount: 92000,
    parameters: ['Specificity', 'Sensitivity', 'Inclusivity', 'Exclusivity', 'Robustness'],
    remarks: 'EMA Ph. Eur. 2.6.7 compliance; 22 mycoplasma species panel',
  },
  {
    id: 'mock-req-2026-02', requestCode: 'REQ-2026-002', title: 'Release Testing — Semaglutide 0.5mg Injection (Pen)',
    type: 'Release Testing', status: 'in_progress', priority: 'urgent',
    lab: { name: 'Bengaluru R&D Analytical Centre' },
    requester: { name: 'Meena Iyer' },
    requestDate: '2026-02-10', dueDate: '2026-03-05',
    quoteAmount: 45000,
    parameters: ['Identity (HPLC)', 'Assay', 'Purity', 'Sterility', 'Endotoxins', 'Delivered Volume'],
  },
  {
    id: 'mock-req-2026-03', requestCode: 'REQ-2026-003', title: 'Stability Study — Rivaroxaban 20mg 36-Month Long-Term',
    type: 'Stability', status: 'submitted', priority: 'normal',
    lab: { name: 'Mumbai QC Central' },
    requester: { name: 'Dr. Anjali Mehta' },
    requestDate: '2026-01-20', dueDate: '2029-01-20',
    quoteAmount: 110000,
    parameters: ['Assay', 'Degradation Products', 'Dissolution', 'Moisture Content', 'Appearance'],
    remarks: 'ICH Q1A 36-month shelf life confirmation; HDPE bottle and blister pack',
  },
  {
    id: 'mock-req-2026-04', requestCode: 'REQ-2026-004', title: 'Microbiology — Bioburden Testing for Medical Devices (Class IIa)',
    type: 'Microbiology', status: 'under_review', priority: 'high',
    lab: { name: 'Delhi Contract Microbiology Lab' },
    requester: { name: 'Ramesh Kulkarni' },
    requestDate: '2026-03-05', dueDate: '2026-04-30',
    quoteAmount: 27500,
    parameters: ['Bioburden Count', 'Identification of Isolates', 'SAL Calculation', 'Sterility Assurance'],
  },
  {
    id: 'mock-req-2026-05', requestCode: 'REQ-2026-005', title: 'Analytical — Extractables & Leachables Study for Prefilled Syringe',
    type: 'Analytical', status: 'in_progress', priority: 'high',
    lab: { name: 'Hyderabad API Lab' },
    requester: { name: 'Deepak Rao' },
    requestDate: '2026-02-25', dueDate: '2026-06-30',
    quoteAmount: 88000,
    parameters: ['Volatile Organics (HS-GC-MS)', 'Semi-Volatiles (LC-MS)', 'Metals (ICP-MS)', 'Non-Volatiles'],
    remarks: 'ISO 10993-18 and USP <1663>/<1664> framework; silicone-lubricated syringe',
  },
  {
    id: 'mock-req-2026-06', requestCode: 'REQ-2026-006', title: 'Release Testing — Empagliflozin 10mg Tablets Batch E26009',
    type: 'Release Testing', status: 'cancelled', priority: 'low',
    lab: { name: 'Pune Formulation Lab' },
    requester: { name: 'Priya Nair' },
    requestDate: '2026-03-20', dueDate: '2026-04-05',
    quoteAmount: 14000,
    parameters: ['Identification', 'Assay', 'Dissolution', 'Uniformity of Weight', 'Disintegration'],
    remarks: 'Cancelled — batch superseded by revised formulation; resubmission expected Q2 2026',
  },
];

export default function Requests() {
  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(() => searchParams.get('status') ?? '');
  const [priorityFilter, setPriorityFilter] = useState(() => searchParams.get('priority') ?? '');
  const [page, setPage] = useState(1);
  const [showCreate, setShowCreate] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<TestRequest | null>(null);
  const [form, setForm] = useState({
    title: '', type: '', labId: '', partnerId: '', priority: 'normal',
    dueDate: '', quoteAmount: '', parameters: '', remarks: '',
  });
  const queryClient = useQueryClient();

  const exportColumns = [
    { key: 'requestCode', label: 'Request ID' },
    { key: 'title', label: 'Title' },
    { key: 'status', label: 'Status' },
    { key: 'priority', label: 'Priority' },
    { key: 'labName', label: 'Lab' },
    { key: 'createdAt', label: 'Created' },
  ];
  const { data, isLoading } = useQuery({
    queryKey: ['requests', { search, status: statusFilter, priority: priorityFilter, page }],
    queryFn: () =>
      api.get('/requests', { params: { search, status: statusFilter, priority: priorityFilter, page, limit: 20 } })
        .then((r) => r.data),
  });

  const { data: monthlyVolume, isLoading: volumeLoading } = useQuery({
    queryKey: ['requests-monthly-volume'],
    queryFn: () => api.get('/requests/monthly-volume').then((r) => r.data.data),
  });

  const { data: byLab } = useQuery({
    queryKey: ['requests-by-lab'],
    queryFn: () => api.get('/requests/by-lab').then((r) => r.data.data),
  });

  const { data: labs } = useQuery({
    queryKey: ['labs-all'],
    queryFn: () => api.get('/labs/all').then((r) => r.data.data),
  });

  const { data: partners } = useQuery({
    queryKey: ['partners-list'],
    queryFn: () => api.get('/partners', { params: { limit: 100 } }).then((r) => r.data.data),
  });

  const createMutation = useMutation({
    mutationFn: (payload: typeof form) =>
      api.post('/requests', {
        ...payload,
        parameters: payload.parameters.split(',').map((p) => p.trim()).filter(Boolean),
        quoteAmount: payload.quoteAmount ? Number(payload.quoteAmount) : undefined,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['requests'] });
      setShowCreate(false);
    },
  });

  const formatINR = (amount: number) =>
    new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount);

  const { exportCSV, exportPDF } = useTableExport(data?.data ?? [], exportColumns, 'test-requests');

  const columns: Column<Record<string, unknown>>[] = [
    { key: 'requestCode', label: 'Request ID', width: 'w-32',
      render: (row) => <span className="font-mono text-xs text-text-muted">{String(row.requestCode)}</span> },
    { key: 'title', label: 'Request',
      render: (row) => (
        <div>
          <p className="font-medium text-text text-sm">{String(row.title)}</p>
          <p className="text-xs text-text-dim">{String(row.type || '—')}</p>
        </div>
      )},
    { key: 'priority', label: 'Priority', render: (row) => <PillBadge value={String(row.priority)} /> },
    { key: 'status', label: 'Status', render: (row) => <PillBadge value={String(row.status)} showDot /> },
    { key: 'lab', label: 'Lab',
      render: (row) => {
        const lab = row.lab as { name: string } | null;
        const partner = row.partner as { name: string } | null;
        return <span className="text-sm text-text">{lab?.name || partner?.name || '—'}</span>;
      }},
    { key: 'dueDate', label: 'Due Date',
      render: (row) => {
        const date = new Date(String(row.dueDate));
        const overdue = date < new Date() && String(row.status) !== 'completed';
        return (
          <span className={`text-xs font-mono ${overdue ? 'text-risk-high font-semibold' : 'text-text'}`}>
            {format(date, 'd MMM yyyy')}
          </span>
        );
      }},
    { key: 'quoteAmount', label: 'Quote', align: 'right',
      render: (row) => {
        const amt = Number(row.quoteAmount);
        return amt > 0 ? (
          <span className={`text-xs font-mono font-medium ${amt > 50000 ? 'text-teal' : 'text-text'}`}>
            {formatINR(amt)}
          </span>
        ) : <span className="text-text-dim text-xs">—</span>;
      }},
  ];

  const handleExport = () => {
    const rows = data?.data as TestRequest[] || [];
    const csv = ['Code,Title,Type,Status,Priority,Lab,Due Date,Quote Amount'].concat(
      rows.map((r) => `${r.requestCode},"${r.title}",${r.type},${r.status},${r.priority},"${r.lab?.name || r.partner?.name || ''}",${r.dueDate},${r.quoteAmount || ''}`)
    ).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'test-requests.csv'; a.click();
  };

  return (
    <div className="space-y-5">
      <PageHeader
        title="Test Requests"
        subtitle={`${data?.meta?.total || 0} test requests`}
        actions={
          <>
            <button onClick={exportCSV} style={{ display:'flex', alignItems:'center', gap:4, padding:'6px 12px', borderRadius:8, background:'#F0FDF4', border:'1px solid #16A34A30', color:'#16A34A', fontSize:12, fontWeight:600, cursor:'pointer' }}>
              <Download size={13} /> CSV
            </button>
            <button onClick={exportPDF} style={{ display:'flex', alignItems:'center', gap:4, padding:'6px 12px', borderRadius:8, background:'#EFF6FF', border:'1px solid #2563EB30', color:'#2563EB', fontSize:12, fontWeight:600, cursor:'pointer' }}>
              <FileText size={13} /> PDF
            </button>
            <button onClick={() => setShowCreate(true)} className="btn-primary"><Plus size={13} /> New Request</button>
          </>
        }
      />

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {volumeLoading ? <ChartSkeleton height={200} /> : (
          <ChartCard title="Monthly Request Volume" subtitle="Requests submitted per month" height={200}>
            <ResponsiveContainer>
              <AreaChart data={monthlyVolume || []} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                <defs>
                  <linearGradient id="reqGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#b07d1a" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#b07d1a" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#C6D8D8" strokeOpacity={0.5} />
                <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#6899A0' }} />
                <YAxis tick={{ fontSize: 10, fill: '#6899A0' }} width={25} allowDecimals={false} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
                <Area type="monotone" dataKey="count" stroke="#b07d1a" strokeWidth={2} fill="url(#reqGrad)" name="Requests" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        )}

        <ChartCard title="Requests by Lab" subtitle="Volume breakdown per lab" height={200}>
          <ResponsiveContainer>
            <BarChart data={byLab || []} layout="vertical" margin={{ top: 5, right: 20, bottom: 5, left: 60 }} barSize={12}>
              <CartesianGrid strokeDasharray="3 3" stroke="#C6D8D8" strokeOpacity={0.5} horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 10, fill: '#6899A0' }} />
              <YAxis type="category" dataKey="lab" tick={{ fontSize: 9, fill: '#6899A0' }} width={60} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 8 }} />
              <Bar dataKey="count" name="Requests" fill="#b07d1a" radius={[0, 3, 3, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-dim" />
          <input placeholder="Search requests..." value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }} className="input pl-8" />
        </div>
        <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }} className="select w-36">
          <option value="">All Status</option>
          <option value="submitted">Submitted</option>
          <option value="under_review">Under Review</option>
          <option value="in_progress">In Progress</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select value={priorityFilter} onChange={(e) => { setPriorityFilter(e.target.value); setPage(1); }} className="select w-36">
          <option value="">All Priority</option>
          <option value="urgent">Urgent</option>
          <option value="high">High</option>
          <option value="normal">Normal</option>
          <option value="low">Low</option>
        </select>
      </div>

      <DataTable
        columns={columns}
        data={(data?.data ?? MOCK_REQUESTS) as Record<string, unknown>[]}
        loading={isLoading}
        onRowClick={(row) => setSelectedRequest(row as unknown as TestRequest)}
        total={data?.meta?.total}
        page={page}
        pageSize={20}
        onPageChange={setPage}
      />

      {/* Detail Drawer */}
      <SideDrawer isOpen={!!selectedRequest} onClose={() => setSelectedRequest(null)}
        title={selectedRequest?.requestCode || ''} subtitle={selectedRequest?.title} width={480}>
        {selectedRequest && (
          <div className="space-y-5">
            <div className="flex items-center gap-2 flex-wrap">
              <PillBadge value={selectedRequest.status} showDot />
              <PillBadge value={selectedRequest.priority} />
              {selectedRequest.quoteAmount && selectedRequest.quoteAmount > 50000 && (
                <span className="flex items-center gap-1 text-xs bg-teal/10 text-teal px-2 py-0.5 rounded-full font-medium">
                  <TrendingUp size={10} /> High Value
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { label: 'Lab / Partner', value: selectedRequest.lab?.name || selectedRequest.partner?.name || '—' },
                { label: 'Requested By', value: selectedRequest.requester?.name || '—' },
                { label: 'Request Date', value: format(new Date(selectedRequest.requestDate), 'd MMM yyyy') },
                { label: 'Due Date', value: format(new Date(selectedRequest.dueDate), 'd MMM yyyy') },
              ].map((item) => (
                <div key={item.label} className="p-3 bg-bg rounded-lg">
                  <p className="text-xs text-text-muted mb-0.5">{item.label}</p>
                  <p className="text-sm font-medium text-text">{item.value}</p>
                </div>
              ))}
            </div>
            {selectedRequest.quoteAmount && (
              <div className="flex items-center gap-2 p-3 rounded-lg bg-teal/5 border border-teal/20">
                <IndianRupee size={14} className="text-teal flex-shrink-0" />
                <p className="text-sm font-semibold text-teal">{formatINR(selectedRequest.quoteAmount)}</p>
                {selectedRequest.quoteAmount > 50000 && (
                  <span className="ml-auto text-xs text-text-muted">Auto-workflow triggered</span>
                )}
              </div>
            )}
            {selectedRequest.parameters && selectedRequest.parameters.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-text-muted mb-2">Test Parameters</p>
                <div className="flex flex-wrap gap-1.5">
                  {selectedRequest.parameters.map((p) => <TagChip key={p} label={p} color="teal" />)}
                </div>
              </div>
            )}
            {selectedRequest.remarks && (
              <div>
                <p className="text-xs font-semibold text-text-muted mb-1">Remarks</p>
                <p className="text-sm text-text leading-relaxed">{selectedRequest.remarks}</p>
              </div>
            )}
          </div>
        )}
      </SideDrawer>

      {/* Create Modal */}
      <Modal isOpen={showCreate} onClose={() => setShowCreate(false)} title="New Test Request"
        size="lg"
        footer={
          <>
            <button onClick={() => setShowCreate(false)} className="btn-secondary">Cancel</button>
            <button onClick={() => createMutation.mutate(form)} disabled={createMutation.isPending} className="btn-primary">
              {createMutation.isPending ? 'Submitting...' : 'Submit Request'}
            </button>
          </>
        }
      >
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="label">Request Title *</label>
            <input className="input" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} />
          </div>
          <div>
            <label className="label">Test Type</label>
            <input className="input" value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))} placeholder="e.g. Stability, Assay" />
          </div>
          <div>
            <label className="label">Priority</label>
            <select className="select" value={form.priority} onChange={(e) => setForm((f) => ({ ...f, priority: e.target.value }))}>
              {['low', 'normal', 'high', 'urgent'].map((p) => <option key={p} value={p}>{p.charAt(0).toUpperCase() + p.slice(1)}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Lab</label>
            <select className="select" value={form.labId} onChange={(e) => setForm((f) => ({ ...f, labId: e.target.value }))}>
              <option value="">Select lab...</option>
              {(labs || []).map((l: { id: string; name: string }) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Partner</label>
            <select className="select" value={form.partnerId} onChange={(e) => setForm((f) => ({ ...f, partnerId: e.target.value }))}>
              <option value="">Select partner...</option>
              {(partners || []).map((p: { id: string; name: string }) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className="label">Due Date *</label>
            <input type="date" className="input" value={form.dueDate} onChange={(e) => setForm((f) => ({ ...f, dueDate: e.target.value }))} />
          </div>
          <div>
            <label className="label">Quote Amount (₹)</label>
            <input type="number" className="input" value={form.quoteAmount} onChange={(e) => setForm((f) => ({ ...f, quoteAmount: e.target.value }))}
              placeholder="Auto-triggers WF-005 if > ₹50,000" />
          </div>
          <div className="col-span-2">
            <label className="label">Test Parameters (comma-separated)</label>
            <input className="input" value={form.parameters} onChange={(e) => setForm((f) => ({ ...f, parameters: e.target.value }))} placeholder="e.g. pH, Viscosity, Assay" />
          </div>
          <div className="col-span-2">
            <label className="label">Remarks</label>
            <textarea className="input resize-none" rows={2} value={form.remarks} onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))} />
          </div>
        </div>
      </Modal>
    </div>
  );
}
